<!-- <svelte:options customElement="clever-search-suggestion" /> -->

<script lang="ts">
  /** store*/
  import {
    state_searchString,
    state_searchSuggestion,
    state_selectedIndicesList,
  } from '../store/state';
  import { ConfigStore } from './../store/config';

  import { WindowsCustomEventTypes } from '../enums/windows-custom-event.enum';
  import type { IRequestSearch } from '../interfaces/request-search-question.interface';
  import type { ISearchSuggestion } from '../interfaces/search-suggestion.interface';
  import { defaultLogger as logger } from '../telemetry/logger';
  import { onMount, tick } from 'svelte';
  import { WidgetType } from '../enums/widget-type.enum';

  export let isautosuggestionup;

  export let focusedIndex: number = -1;
  let wrapperEl: HTMLDivElement;

  let lastAnnouncedFocusIndex = -1;

  $: announceFocusedSuggestion(focusedIndex, $state_searchSuggestion);

  const announceFocusedSuggestion = (
    index: number,
    suggestions: ISearchSuggestion[] | undefined
  ) => {
    if (index < 0 || !suggestions || index >= suggestions.length) {
      lastAnnouncedFocusIndex = -1;
      return;
    }
    if (index === lastAnnouncedFocusIndex) return;
    lastAnnouncedFocusIndex = index;
    const question = suggestions[index]?.data?.question;
    if (!question) return;
    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.announceToScreenReader, {
        detail: { message: question },
      })
    );
  };

  // onMount(() => {
  //     shadowDomUpdates("clever-search-suggestion", true);
  // });

  const escapeRegExp = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const highlightSuggestion = (question: string): string => {
    const searchString = $state_searchString ?? '';
    if (!searchString) return escapeHtml(question);

    const regex = new RegExp(escapeRegExp(searchString), 'gi');
    let highlighted = '';
    let lastIndex = 0;

    question.replace(regex, (match, offset) => {
      highlighted += escapeHtml(question.slice(lastIndex, offset));
      highlighted += `<b>${escapeHtml(match)}</b>`;
      lastIndex = offset + match.length;
      return match;
    });

    return highlighted + escapeHtml(question.slice(lastIndex));
  };

  const searchSuggestionItem = (x: ISearchSuggestion): void => {
    const data: IRequestSearch = {
      query: x.data.question,
      flags: $ConfigStore.customAttributes?.flags,
      cookies: $ConfigStore.customAttributes?.cookies,
      url: $ConfigStore.customAttributes?.url,
      requestType: 'typeahead',
    };

    if (x.data.index) {
      data.index = x.data.index;
    } else if ($state_selectedIndicesList.length) {
      data.index = $state_selectedIndicesList.toString().replace(', ', '');
    }

    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.requestSearch, {
        detail: data,
      })
    );

    if ('ontouchstart' in window) {
      setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 100);
    }
  };

  const handleOnKeyUp = (e: { key: any }) => {
    if (!$state_searchSuggestion?.length) return;

    switch (e.key) {
      case 'Enter':
        logger.debug('focusedIndex on Enter keyup is: ', focusedIndex);
        if (focusedIndex >= 0 && focusedIndex < $state_searchSuggestion.length) {
          logger.debug('searching for typeahead ', $state_searchSuggestion[focusedIndex]);
          searchSuggestionItem($state_searchSuggestion[focusedIndex]);
        } else {
          logger.debug('searching for user input ', $state_searchString);
          window.dispatchEvent(
            new CustomEvent(WindowsCustomEventTypes.requestSearch, {
              detail: {
                query: $state_searchString,
                flags: $ConfigStore.customAttributes?.flags,
                cookies: $ConfigStore.customAttributes?.cookies,
                url: $ConfigStore.customAttributes?.url,
              },
            })
          );
        }
        break;
      case 'ArrowDown':
        if (focusedIndex === -1) {
          logger.debug('focusedIndex was -1, setting to 0');
          focusedIndex = 0;
          scrollToFocused();
          break;
        }
        moveNextItem(1);
        break;
      case 'ArrowUp':
        if (focusedIndex === -1) {
          focusedIndex = $state_searchSuggestion.length - 1;
          logger.debug('focusedIndex was -1, setting to last index');
          scrollToFocused();
          break;
        }
        moveNextItem(-1);
        break;
      default:
        break;
    }
  };

  const handleOnKeyDown = (e: {
    key: any;
    shiftKey: boolean;
    preventDefault: () => void;
  }) => {
    if (!$state_searchSuggestion?.length) return;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          if (focusedIndex === -1) {
            focusedIndex = $state_searchSuggestion.length - 1;
          } else if (focusedIndex === 0) {
            focusedIndex = -1;
            state_searchSuggestion.set([]);
          } else {
            moveNextItem(-1);
          }
        } else {
          if (focusedIndex === -1) {
            focusedIndex = 0;
          } else if (focusedIndex === $state_searchSuggestion.length - 1) {
            focusedIndex = -1;
            state_searchSuggestion.set([]);
          } else {
            moveNextItem(1);
          }
        }
        break;
      case 'Escape':
        focusedIndex = -1;
        state_searchSuggestion.set([]);
        break;
    }
  };

  const scrollToFocused = async (): Promise<void> => {
    await tick();
    if (focusedIndex === -1 || !wrapperEl) return;
    const el = wrapperEl.querySelector(`#sc-${focusedIndex}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  };

  const moveNextItem = (direction: number): void => {
    if (direction === 1) {
      if ($state_searchSuggestion[focusedIndex + 1]) {
        focusedIndex += 1;
      } else {
        focusedIndex = -1;
      }
    } else {
      if ($state_searchSuggestion[+focusedIndex - 1]) {
        focusedIndex = focusedIndex - 1;
      } else {
        focusedIndex = -1;
      }
    }
    scrollToFocused();
    logger.debug('moved focus to index ', focusedIndex);
  };
</script>

<div
  bind:this={wrapperEl}
  class={'cs-hint-inner-wrapper' +
    ($ConfigStore.isTextAreaSearch ? ' cs-hint-inner-wrapper-textarea' : '') +
    ($ConfigStore.widgetType === WidgetType.cip ||
    $ConfigStore.widgetType === WidgetType.ce
      ? ' cs-hint-inner-wrapper-cip'
      : '') +
    ($ConfigStore.canClearChatHistory ? ' cs-hint-inner-wrapper-clear' : '')}
  class:cs-hint-up-direction={isautosuggestionup}
>
  <ul id="cs-suggestion-listbox" role="listbox" aria-label="Search suggestions">
    {#each $state_searchSuggestion as suggestionItem, i}
      <li
        role="option"
        aria-selected={focusedIndex == i}
        class:active={focusedIndex == i}
        id={`sc-${i}`}
        on:mousedown|preventDefault={() => searchSuggestionItem(suggestionItem)}
        on:keydown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            searchSuggestionItem(suggestionItem);
          }
        }}
        aria-label={suggestionItem.data.question}
      >
        {@html highlightSuggestion(suggestionItem.data.question)}
      </li>
    {/each}
  </ul>
</div>

<svelte:window on:keyup={handleOnKeyUp} on:keydown={handleOnKeyDown} />

<style lang="scss">
  @use './../../scss/components/_search-input-suggestion';
</style>
